<template>
  <Modal
    v-model:visible="isVisible"
    class="batch-create-success-modal"
    @keypress.esc="isVisible = false"
  >
    <template #header>
      <FontAwesomeIcon aria-hidden="true" :icon="['fal', 'check-circle']" />
      <h1>{{ t('common.success') }}</h1>
    </template>

    <p>
      {{
        t('messages.successSavingParam', {
          param: paramLabel,
          count: ids.length,
        })
      }}
    </p>

    <h2 class="text-gray-darker uppercase">
      {{ t('common.recentParam', { param: paramLabel }) }}
    </h2>

    <ol class="m-0 p-0">
      <li v-for="id in ids" :key="id" class="text-gray-darkest">
        {{
          type === 'Order'
            ? t('orders.orderNumParam', { param: id })
            : t('shipments.shipmentNumParam', { param: id })
        }}
      </li>
    </ol>

    <template #footer>
      <router-link v-if="ids.length" :to="searchRoute">
        <RygenButton
          mode="secondary"
          :label="t('common.viewParam', { param: paramLabel })"
          @click="isVisible = false"
        />
      </router-link>

      <RygenButton
        mode="primary"
        icon="pi pi-check"
        :label="t('common.continue')"
        @click="onDone"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { buildSearchRoute } from '@/util';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  ids: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  type: {
    type: String as PropType<'Order' | 'Shipment'>,
    required: true,
  },
});
const { ids, type } = toRefs(props);
const emit = defineEmits<{
  (event: 'done'): void;
}>();

const { t } = useI18n();

const paramLabel = computed(() =>
  type.value === 'Order'
    ? t('orders.orders', ids.value.length)
    : t('shipments.shipments', ids.value.length),
);

const searchRoute = computed(() =>
  type.value === 'Order'
    ? buildSearchRoute('orderList', { orderId: ids.value })
    : buildSearchRoute('shipmentList', { shipmentId: ids.value }),
);

const onDone = () => {
  isVisible.value = false;
  emit('done');
};
</script>

<style>
.batch-create-success-modal.p-dialog {
  .p-dialog-header {
    .modal-header {
      gap: var(--sp-2);
      align-items: center;
      font-size: 18px;

      svg {
        color: var(--success-color);
      }

      h1 {
        color: var(--brand-dark-color);
      }
    }
  }

  .p-dialog-content {
    padding-block: 0;

    p {
      color: var(--brand-dark-color);
    }

    h2 {
      margin: var(--sp-6) 0 var(--sp-3) 0;
      font-size: 12px;
      font-weight: 500;
    }

    ol {
      counter-reset: itemNum;

      li {
        &::before {
          content: counter(itemNum) '. ';
        }

        counter-increment: itemNum;
        padding: var(--sp-3) 0;
        border-top: var(--divider);
      }
    }
  }
}
</style>
